import React from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import {
  CurrentProductContextProvider,
  EmbeddedTIContextProvider,
  LegalModalContextProvider,
} from 'context';

import Layout from 'components/Layout';
import ScrollBehavior from 'components/ScrollBehavior';

import GlobalDefaults from 'widgets/GlobalDefaults';

import * as Page from 'modules';

import RedirectPageOnCondition from './RedirectPageOnCondition';
import ThirdPartyScriptsLoader from './ThirPartyScriptsLoader';
import routes from './routes';
import { useTIRouterProps } from './useTIRouterProps';

export type RouterProps = {
  initialPath?: string;
};

const Router: React.FC<RouterProps> = ({ initialPath }) => {
  const {
    defaultPath,
    startTracking,
    hasEnabledLoungeServiceProviders,
    hasEnabledParkingServiceProviders,
    isEmbeddedEnabled,
    isMeiliEnabled,
    isLandingPageEnabled,
    isMeiliManagePageEnabled,
    hasEnabledEurail,
  } = useTIRouterProps(initialPath);

  return (
    <BrowserRouter>
      <ScrollBehavior />
      <GlobalDefaults>
        <ThirdPartyScriptsLoader startTracking={startTracking} />
        {isEmbeddedEnabled ? (
          <EmbeddedTIContextProvider>
            <Routes>
              <Route
                path={routes.MEILI_CAR_RENTAL}
                element={
                  <RedirectPageOnCondition
                    condition={isMeiliEnabled}
                    redirectPath={defaultPath}
                    PageElement={<Page.CarRentalPage />}
                  />
                }
              />
            </Routes>
          </EmbeddedTIContextProvider>
        ) : (
          <LegalModalContextProvider>
            <Routes>
              <Route element={<Layout />}>
                {/* PARKING */}
                <Route
                  path={routes.PARKING_SEARCH}
                  element={
                    <RedirectPageOnCondition
                      condition={hasEnabledParkingServiceProviders}
                      redirectPath={defaultPath}
                      PageElement={<Page.ParkingSearchInputPage />}
                    />
                  }
                />
                <Route
                  path={routes.PARKING_SEARCH_RESULTS}
                  element={
                    <RedirectPageOnCondition
                      condition={hasEnabledParkingServiceProviders}
                      redirectPath={defaultPath}
                      PageElement={<Page.ParkingSearchResultsPage />}
                    />
                  }
                />
                <Route
                  element={
                    <CurrentProductContextProvider>
                      <Outlet />
                    </CurrentProductContextProvider>
                  }
                >
                  <Route
                    path={routes.PARKING_PDP}
                    element={
                      <RedirectPageOnCondition
                        condition={hasEnabledParkingServiceProviders}
                        redirectPath={defaultPath}
                        PageElement={<Page.ParkingProductDetailsPage />}
                      />
                    }
                  />
                  <Route
                    path={routes.PARKING_TIP}
                    element={
                      <RedirectPageOnCondition
                        condition={hasEnabledParkingServiceProviders}
                        redirectPath={defaultPath}
                        PageElement={<Page.ParkingTravellerInputPage />}
                      />
                    }
                  />
                  <Route
                    path={routes.PARKING_PIP}
                    element={
                      <RedirectPageOnCondition
                        condition={hasEnabledParkingServiceProviders}
                        redirectPath={defaultPath}
                        PageElement={<Page.ParkingPaymentInputPage />}
                      />
                    }
                  />
                </Route>
                <Route
                  path={routes.PARKING_BCP}
                  element={
                    <RedirectPageOnCondition
                      condition={hasEnabledParkingServiceProviders}
                      redirectPath={defaultPath}
                      PageElement={<Page.ParkingBookingConfirmationPage />}
                    />
                  }
                />
                {/* LOUNGES */}
                <Route
                  path={routes.LOUNGES_SEARCH}
                  element={
                    <RedirectPageOnCondition
                      condition={hasEnabledLoungeServiceProviders}
                      redirectPath={defaultPath}
                      PageElement={<Page.LoungesSearchInputPage />}
                    />
                  }
                />
                <Route
                  path={routes.LOUNGES_SEARCH_RESULTS}
                  element={
                    <RedirectPageOnCondition
                      condition={hasEnabledLoungeServiceProviders}
                      redirectPath={defaultPath}
                      PageElement={<Page.LoungesSearchResultsPage />}
                    />
                  }
                />
                <Route
                  element={
                    <CurrentProductContextProvider>
                      <Outlet />
                    </CurrentProductContextProvider>
                  }
                >
                  <Route
                    path={routes.LOUNGES_PDP}
                    element={
                      <RedirectPageOnCondition
                        condition={hasEnabledLoungeServiceProviders}
                        redirectPath={defaultPath}
                        PageElement={<Page.LoungesProductDetailsPage />}
                      />
                    }
                  />
                  <Route
                    path={routes.LOUNGES_TIP}
                    element={
                      <RedirectPageOnCondition
                        condition={hasEnabledLoungeServiceProviders}
                        redirectPath={defaultPath}
                        PageElement={<Page.LoungesTravellerInputPage />}
                      />
                    }
                  />
                  <Route
                    path={routes.LOUNGES_PIP}
                    element={
                      <RedirectPageOnCondition
                        condition={hasEnabledLoungeServiceProviders}
                        redirectPath={defaultPath}
                        PageElement={<Page.LoungesPaymentInputPage />}
                      />
                    }
                  />
                </Route>
                <Route
                  path={routes.LOUNGES_BCP}
                  element={
                    <RedirectPageOnCondition
                      condition={hasEnabledLoungeServiceProviders}
                      redirectPath={defaultPath}
                      PageElement={<Page.LoungesBookingConfirmationPage />}
                    />
                  }
                />
                <Route
                  path={routes.MEILI_CAR_RENTAL_MANAGE}
                  element={
                    <RedirectPageOnCondition
                      condition={isMeiliManagePageEnabled}
                      redirectPath={defaultPath}
                      PageElement={<Page.CarRentalManagePage />}
                    />
                  }
                />
                {/* EURAIL */}
                <Route
                  path={routes.EURAIL_SEARCH}
                  element={
                    <RedirectPageOnCondition
                      condition={hasEnabledEurail}
                      redirectPath={defaultPath}
                      PageElement={<Page.EurailSearchInputPage />}
                    />
                  }
                />
                <Route
                  path={routes.EURAIL_SEARCH_RESULTS}
                  element={
                    <RedirectPageOnCondition
                      condition={hasEnabledEurail}
                      redirectPath={defaultPath}
                      PageElement={<Page.EurailSearchResultsPage />}
                    />
                  }
                />
                <Route
                  element={
                    <CurrentProductContextProvider>
                      <Outlet />
                    </CurrentProductContextProvider>
                  }
                >
                  <Route
                    path={routes.EURAIL_TIP}
                    element={
                      <RedirectPageOnCondition
                        condition={hasEnabledEurail}
                        redirectPath={defaultPath}
                        PageElement={<Page.EurailTravellerInputPage />}
                      />
                    }
                  />
                  <Route
                    path={routes.EURAIL_PIP}
                    element={
                      <RedirectPageOnCondition
                        condition={hasEnabledEurail}
                        redirectPath={defaultPath}
                        PageElement={<Page.EurailPaymentInputPage />}
                      />
                    }
                  />
                </Route>

                <Route
                  path={routes.STOPOVER_LANDING_OFFERS}
                  element={
                    <RedirectPageOnCondition
                      condition={true}
                      redirectPath={defaultPath}
                      PageElement={<Page.Stopover />}
                    />
                  }
                />

                {/* BASE ROUTE */}
                <Route
                  index
                  element={
                    <RedirectPageOnCondition
                      condition={isLandingPageEnabled}
                      redirectPath={defaultPath}
                      PageElement={<Page.LandingPage />}
                    />
                  }
                />
              </Route>
              <Route path={'*'} element={<Navigate to={defaultPath} />} />
            </Routes>
          </LegalModalContextProvider>
        )}
      </GlobalDefaults>
    </BrowserRouter>
  );
};

export default Router;
